import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { BlogPost } from '../types/BlogTypes';
import { getBlogPostById, formatBlogDate, markdownToHtml } from '../utils/blogUtils';
import ShareButtons from '../components/blog/ShareButtons';
import '../assets/styles/Blog.css';

const BlogPostPage: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [contentHtml, setContentHtml] = useState('');
  const location = useLocation();

  useEffect(() => {
    const loadPost = async () => {
      if (!postId) return;
      
      setLoading(true);
      const postData = await getBlogPostById(postId);
      setPost(postData);
      
      if (postData) {
        // Convert markdown content to HTML
        setContentHtml(markdownToHtml(postData.content));
      }
      
      setLoading(false);
    };
    
    loadPost();
  }, [postId]);

  if (loading) {
    return <div className="blog-container">Loading post...</div>;
  }

  if (!post) {
    return (
      <div className="blog-container">
        <div className="blog-no-results">
          <h3>Blog post not found</h3>
          <p>The blog post you're looking for doesn't exist or has been removed.</p>
          <Link to="/blog" className="blog-back-link">
            &larr; Back to all posts
          </Link>
        </div>
      </div>
    );
  }

  // Generate full URL for sharing
  const fullUrl = window.location.origin + location.pathname;

  return (
    <div className="blog-container">
      <Helmet>
        <title>{post.title} | Price on Rails Blog</title>
        <meta name="description" content={post.summary} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.summary} />
        <meta property="og:image" content={post.image} />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.summary} />
        <meta name="twitter:image" content={post.image} />
      </Helmet>

      <Link to="/blog" className="blog-back-link">
        <span className="blog-back-icon">&larr;</span> Back to all posts
      </Link>
      
      <article className="blog-post">
        <div className="blog-post-header">
          <h1 className="blog-post-title">{post.title}</h1>
          <div className="blog-post-meta">
            <span className="blog-post-date">{formatBlogDate(post.date)}</span>
            <span className="blog-post-author">By {post.author}</span>
          </div>
        </div>
        
        <img 
          src={post.image} 
          alt={post.title} 
          className="blog-post-image"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/images/blog/default.jpg';
          }}
        />
        
        <div 
          className="blog-post-content"
          dangerouslySetInnerHTML={{ __html: contentHtml }}
        />
        
        <ShareButtons 
          title={post.title}
          url={fullUrl}
          summary={post.summary}
        />
        
        {post.tags && post.tags.length > 0 && (
          <div className="blog-post-tags">
            <span className="blog-post-tags-label">Related topics:</span>
            {post.tags.map(tag => (
              <Link key={tag} to={`/blog?tag=${tag}`} className="blog-tag">
                {tag}
              </Link>
            ))}
          </div>
        )}
      </article>
    </div>
  );
};

export default BlogPostPage; 